import { html, css, OmniElement, OmniIcon, OmniStyle } from 'omni-ui';

import { om2alert } from './om2-alert.js';
import { Om2FormField } from './om2-form-field.js';
import { mergeSchema } from './om2-form-field/common/util.js';
import { getSSOEnv } from './om2-util.js';

OmniIcon.register();
OmniStyle.register();
Om2FormField.register();

/**
 * Works in concert with Om2FormField to render schema/uiSchema objects. This class
 * is the parent "form" while Om2FormField handles rendering all the possible
 * individual input fields.
 *
 * @extends OmniElement
 */
export class Om2Form extends OmniElement {
  /**
   * @property {string} env - The environment (dev, qa, etc)
   * @property {object} schema - This is defines the input fields with minimal formatting information.
   * @property {object} uiSchema - This defines overall layout and formatting and references fields in schema.
   * @property {object} data - Form data saved previously or otherwise used to initialize
   * @property {object} context - Used to pass additional data, namely "readonly" & some internal data
   * @property {function} save - Callback function to save data
   * @property {boolean} hideSubmit - Optionally hide the Save button
   * @property {string} submitButtonText - Optionally change the button text (default Save)
   * @property {object} changes - (Internal) Stored changes to the form
   * @property {boolean} isSubmitting - (Internal) Stored changes to the form
   */
  static get properties() {
    return {
      env: { type: String },
      schema: { type: Object },
      uiSchema: { type: Object },
      data: { type: Object },
      context: { type: Object },
      save: { attribute: false },
      hideSubmit: { type: Boolean },
      submitButtonText: { type: String },
      changes: { state: true },
      isSubmitting: { state: true },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .omni .box:not(:last-child) {
          margin-bottom: 2.143rem;
        }
        .omni .box {
          padding: 2.14rem 2.86rem 4.29rem 2.86rem;
        }
        .omni .error input,
        .omni .error select,
        .omni .error textarea,
        .error om2-dropdown-impl {
          border-color: var(--color-error) !important;
        }
        om2-form-field {
          display: block;
        }
        .omni om2-form-field a .title {
          color: var(--color-primary);
        }
        om2-form-field > .error .message * {
          font-size: 12px;
          vertical-align: top;
          fill: var(--color-error);
        }

        /* Nested arrays end up with extra margins that we remove here */
        fieldset.array fieldset.array {
          margin-bottom: -1.5rem;
        }
      `,
    ];
  }

  static register(name = 'om2-form') {
    return super.register(name, Om2Form);
  }

  constructor() {
    super();
    this.hideSubmit = false;
    this.changes = {};
    this.env = getSSOEnv();
    this.DUMMY_OBJECT = []; // This object array indicates we are mid-reset in renderArray()
    this.changeListeners = [];
  }

  /**
   * Called from outside this component.
   * Triggers saving of the form if it is in a valid state.
   */
  async submit(e) {
    try {
      this.isSubmitting = true;
      e?.preventDefault();
      if (this.validate() && this.save) {
        await this.save({ ...this.data, ...this.changes });
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * When submit is triggered internally, catch and display any exceptions
   *
   * @async
   * @param {event} e - The event triggering the submit
   */
  async _submitOrAlert(e) {
    try {
      await this.submit(e);
    } catch (ex) {
      om2alert(ex);
    }
  }

  /**
   * Called from outside this component.
   * Should be called immediately before submit() to determine if the form is valid.
   *
   * @returns {boolean} Returns whether the form is valid / ready to save
   */
  validate() {
    return this.shadowRoot.querySelector('form').reportValidity();
  }

  /**
   * Called from outside this component.
   * Allows clearing any changed state for a particular field.
   *
   * @param {string} key - The key identifying the field to reset
   */
  resetField(key, newValue) {
    delete this.changes[key];
    if (newValue !== undefined) this.changes[key] = newValue;
  }

  /**
   * Sends event to indicate that the form was canceled.
   */
  _cancel(e) {
    e?.preventDefault();
    this.dispatchNewEvent('om2-form-cancel');
  }

  /**
   * Handles individual form fields @input events.
   * The new value is saved into this.changes & we dispatch a change event
   *
   * @param {event} e - The form field event
   */
  _onFormInput(e) {
    // trap and handle built-in event (suppress bubbling)
    e.stopPropagation();

    const key = e?.detail?.name ?? e.target.name;
    const value =
      e?.detail?.value ??
      e.composedPath()[0][e.target.type === 'checkbox' ? 'checked' : 'value'];
    if (key in this.changes && this.changes[key] === value) return; // Ignore events that don't change anything

    this.changes = { ...this.changes, [key]: value };

    // Only trigger change for user-generated inputs
    if (!e?.detail?.isAutoGenerated) {
      this._dispatchChange(key, value);
    }

    // Update any listeners
    this.changeListeners[key]?.forEach(listener => {
      try {
        listener.callback(key, value);
      } catch (ex) {
        // eslint-disable-next-line no-console
        console.log(`Exception thrown in form field listener callback`, ex);
      }
    });
  }

  /**
   * Add a change event listener for a specific field. Optionally wrap the function with
   * debounce logic. When the specified field is changed in the future, the callback
   * will be invoked. Also if immediate is true (default) the callback will be called
   * with the current field value.
   *
   * @param {Event} e - The add event, must contain name and callback in the detail
   */
  _addFieldChangeListener(e) {
    function debounceFunc(f, timeout) {
      if (!timeout) return f;
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(
          () => {
            f.apply(this, args);
          },
          Number.isFinite(timeout) ? timeout : 250
        );
      };
    }

    e.stopPropagation();
    const { field, callback, immediate = true, debounce = true } = e.detail;
    if (!this.changeListeners[field]) this.changeListeners[field] = [];

    // Ignore if the same source is already listening to this field
    if (
      this.changeListeners[field].some(listener => listener.source === e.target)
    )
      return;

    const debounced = debounceFunc(callback, debounce);
    this.changeListeners[field].push({
      callback: debounced,
      source: e.target,
    });

    // In addition to registering for future changes, if immediate is true (default)
    // also send the current value (might be undefined).
    if (immediate) debounced(field, { ...this.data, ...this.changes }[field]);
  }

  /**
   * Send a change event indicating the form has changed state. This function should be
   * modified with care as child classes can override it to customize how exactly to
   * dispatch this event.
   *
   * @param {string} key - The key (id) corresponding to the changed field
   * @param {object} value - The value of the changed field
   */
  _dispatchChange(key, value) {
    this.dispatchNewEvent('change', { detail: { key, value } });
  }

  /**
   * Allow overriding a ui item schema depending on some condition. Each item in the
   * list is evaluated independently and if true the "then" dictionary is merged
   * overtop the ui item.
   *
   * @param {object} uiItem - The ui item object
   * @return {object} The resulting object (or the original if no override was needed)
   *
   * @example
   *
   *   // This hides ui item if field "field_id" has a value not equal to "other"
   *   overrideIf: [
   *     {
   *       if: { id: 'field_id', op: '!==', value: 'other'},
   *       then: { class: 'is-hidden' }
   *     }
   *   ]
   */
  applyConditionalOverrides(uiItem) {
    let result = uiItem;

    // Support nested keys (as arrays) in addition to normal key lookup
    const getVal = (source, key) => {
      if (Array.isArray(key)) {
        let value = source;
        key.forEach(keyPart => {
          value = value?.[keyPart];
        });
        return value;
      }
      return source?.[key];
    };

    (uiItem?.overrideIf ?? []).forEach(config => {
      const key = config.if.id;
      const fieldValue = getVal(this.changes, key) ?? getVal(this.data, key);
      let condition;
      if (config.if === true) condition = true;
      else if (config.if === false) condition = false;
      else {
        switch (config.if.op) {
          case '===':
            condition = fieldValue === config.if.value;
            break;
          case '!==':
            condition = fieldValue !== config.if.value;
            break;
          case '!':
            condition = !fieldValue;
            break;
          case '!!':
            condition = !!fieldValue;
            break;
          case 'in':
            condition = !!config.if.value?.includes(fieldValue);
            break;
          case 'not in':
            condition = !config.if.value?.includes(fieldValue);
            break;
          default:
            throw Error(
              `Unknown condition operator "${config.if.op}" in overrideIf`
            );
        }
      }
      if (condition) result = mergeSchema(result, config.then);
    });

    return result;
  }

  /**
   * Recursively render a list of UI Items
   *
   * @param {array} schemaItems - The list of UI Items to render
   * @param {object} data - The portion of saved data associated with this list
   * @param {object} [context] - additional context object
   * @returns {html} The rendered output
   */
  _renderUiItems(schemaItems, data, context = {}) {
    return Array.isArray(schemaItems)
      ? schemaItems.map(schemaItem =>
          this._renderUiItem(schemaItem, data, context)
        )
      : this._renderUiItem(schemaItems, data, context);
  }

  /**
   * Recursively render a single UI Item
   *
   * @param {object} uiItem - A single UI Item to render
   * @param {object} data - The saved data associated with this UI Item
   * @param {object} context - additional context object
   * @returns {html} The rendered output
   */
  _renderUiItem(uiItem, data, context) {
    // eslint-disable-next-line no-param-reassign
    uiItem = this.applyConditionalOverrides(uiItem);

    switch (uiItem?.type) {
      case 'array':
        return this._renderArray(uiItem, data, context);
      case 'box':
        return html`<div class="box ${uiItem.class || ''}">
          ${this._renderUiItems(uiItem.content, data, context)}
        </div>`;
      case 'row':
        return html`<div class="columns ${uiItem.class || ''}">
          ${this._renderUiItems(uiItem.content, data, context)}
        </div>`;
      case 'column':
        return html`<div class="column ${uiItem.class || ''}">
          ${this._renderUiItems(uiItem.content, data, context)}
        </div>`;
      case 'ref': {
        if (!this.schema[uiItem.id]) {
          return html`<p class="has-text-danger">
            Invalid ref id="${uiItem.id}"
          </p>`;
        }
        // Everything except for id/type considered an override
        const overrides = { ...uiItem };
        delete overrides.id;
        delete overrides.type;

        // Sometimes we may need the key used to access/store data to be different than
        // the id used to uniquely lookup an input field from the schema. Specifically
        // this comes up with array fields. If the form has as an "id" input and also the
        // array has an "id" input -- we may change the second id to something unique
        // like "__array_id" but then set (key: "id") to specify that it should still
        // store itself as "id" within the array.
        const key = uiItem.key ?? uiItem.id;
        return this._renderField(
          mergeSchema(this.schema[uiItem.id], overrides),
          key,
          data[key],
          context
        );
      }
      case 'button':
      case 'title':
      case 'text':
      case 'horizontal-rule':
      case 'fixed-image':
        return this._renderField(uiItem, '', undefined, context);
      default:
        return html`<p class="is-italic">
          Unimplemented ui schema type: ${uiItem?.type}
        </p>`;
    }
  }

  /**
   * Render an Array UI Item.
   *
   * @param {object} schema - The array schema (UI Item) to render
   * @param {object} data - The saved data associated with this array
   * @param {object} context - additional context object
   * @returns {html} The rendered output
   */
  _renderArray(schema, data, context) {
    //  TODO: Pull this massive function out to its own file
    let arrayData = Array.isArray(data[schema.id]) ? data[schema.id] : [];
    const delim = '::';

    const dispatch = async detail => {
      // Update outside of current render() so property changes will be detected by lit
      await new Promise(resolve => {
        setTimeout(resolve, 0);
      }); // Wait for next clock tick
      if (context.suffix) {
        // Nested array: recursively propagate event to build up datastructure 'value'
        const targetElem = this.shadowRoot.querySelector(
          `fieldset.array[array-id="${schema.id}${context.suffix}"]`
        );
        const name = `${detail.name}${context.suffix}`;
        const event = new CustomEvent('input', {
          detail: { ...detail, name },
          bubbles: true,
        });
        targetElem.dispatchEvent(event);
      } else {
        // Root array: just call om2-form's _onFormInput()
        this._onFormInput(new CustomEvent('input', { detail }));
      }
      this.requestUpdate();
      await this.updateComplete;
    };

    // Initialize array data if length is less than minRows
    if (
      arrayData.length < (schema.minRows ?? 0) &&
      arrayData !== this.DUMMY_OBJECT
    ) {
      arrayData = arrayData.slice();
      while (arrayData.length < (schema.minRows ?? 0)) arrayData.push({});

      dispatch({
        name: schema.id,
        value: arrayData,
        isAutoGenerated: true,
      });
    }

    // Intercept child input events and re-dispatch new event with array data
    // Storing the array index in the field name avoids issues with multiple unrelated
    // fields with the same name (looking at you radio button).
    const handleInput = e => {
      e.stopPropagation();
      const [, name, index] =
        (e?.detail?.name ?? e.target.name).match(
          new RegExp(`^(.*)${delim}(.*)$`)
        ) ?? [];
      const propName = name.split(delim)[0];
      const value =
        e?.detail?.value ??
        e.composedPath()[0][e.target.type === 'checkbox' ? 'checked' : 'value'];
      const isAutoGenerated = e?.detail?.isAutoGenerated;

      const newdata = arrayData.slice();
      newdata[index] = newdata[index] ? { ...newdata[index] } : {};
      newdata[index][propName] = value;

      dispatch({ name: schema.id, value: newdata, isAutoGenerated });
    };

    // Remove a row at a particular index
    const removeRow = async (e, index) => {
      e.stopPropagation();
      e.preventDefault();
      const newdata = arrayData.slice();
      newdata.splice(index, 1);

      // This is to force lit-element to discard dom elements and re-render. This avoids
      // issues with stale dom elements. The repeat() directive would be a good fit if
      // our data had ids. Using the object identity as the repeat key also has issues
      // where it causes re-rendering while a user is typing (and blurs focus).
      await dispatch({ name: schema.id, value: this.DUMMY_OBJECT });
      await dispatch({ name: schema.id, value: newdata });
    };

    // Add a row (to the end)
    const addRow = e => {
      e.stopPropagation();
      e.preventDefault();
      const newdata = arrayData.slice();
      newdata.push({});
      dispatch({ name: schema.id, value: newdata });
    };

    const getMarginStyle = titleHidden =>
      titleHidden === undefined || titleHidden ? '' : `margin-top: 2rem`;

    return html`
      <fieldset class="array" array-id="${schema.id}${context.suffix}">
        <div
          @input=${e => handleInput(e)}
          @removeRow=${e => removeRow(e)}
          @addRow=${e => addRow(e)}
        >
          ${arrayData.map((childData, index) => {
            const titleHidden =
              schema.hideChildTitles || context.titleHidden || index !== 0;
            const buttonMargin = getMarginStyle(titleHidden);
            const hideRemoveButton = arrayData.length <= (schema.minRows ?? 0);
            return html`
              <div class="columns">
                ${schema.content.map(
                  child => html`
                    ${this._renderUiItem(child, childData, {
                      ...context,
                      suffix: `${context.suffix ?? ''}${delim}${index}`,
                      titleHidden,
                      arrayIndex: index,
                      arrayLength: arrayData.length,
                    })}
                  `
                )}
                ${this.context?.readonly
                  ? null
                  : html`
                      <div class="column is-narrow">
                        <button
                          class="button ${schema?.removeButton?.class ??
                          ''} ${hideRemoveButton ? 'is-hidden' : ''}"
                          style=${buttonMargin}
                          @click=${e => removeRow(e, index)}
                        >
                          ${schema?.removeButton?.icon
                            ? html`<omni-icon
                                icon-id=${schema.removeButton.icon.id}
                                class=${schema?.removeButton.icon.class ?? ''}
                              ></omni-icon>`
                            : ''}
                          ${schema?.removeButton?.text
                            ? html`<span>${schema?.removeButton?.text}</span>`
                            : ''}
                        </button>
                      </div>
                      <div class="column is-narrow">
                        <button
                          class="button ${schema?.addButton?.class ??
                          ''} ${index === arrayData.length - 1
                            ? ''
                            : 'is-invisible'}"
                          style=${buttonMargin}
                          @click=${e => addRow(e)}
                        >
                          ${schema?.addButton?.icon
                            ? html`<omni-icon
                                icon-id=${schema.addButton.icon.id}
                                class=${schema?.addButton.icon.class ?? ''}
                              ></omni-icon>`
                            : ''}
                          ${schema?.addButton?.text
                            ? html`<span>${schema?.addButton?.text}</span>`
                            : ''}
                        </button>
                      </div>
                    `}
              </div>
            `;
          })}
          <button
            class="button ${schema?.addButton?.class ??
            ''} ${arrayData.length === 0 && !this.context?.readonly
              ? ''
              : 'is-hidden'}"
            style="${getMarginStyle(context?.titleHidden)}"
            @click=${e => addRow(e)}
          >
            ${schema?.addButton?.icon
              ? html`<omni-icon
                  icon-id=${schema.addButton.icon.id}
                  class=${schema?.addButton.icon.class ?? ''}
                ></omni-icon>`
              : ''}
            ${schema?.addButton?.text
              ? html`<span>${schema?.addButton?.text}</span>`
              : ''}
          </button>
        </div>
      </fieldset>
    `;
  }

  /**
   * Render a form input field -- uses Om2FormField.
   *
   * @param {object} schema - The array schema (UI Item) to render
   * @param {string} key - The key (id) that the field should use to identify itself
   * @param {object} data - The saved data associated with this field
   * @param {object} context - additional context object
   * @returns {html} The rendered output
   */
  _renderField(schema, key, data, context) {
    return html`
      <om2-form-field
        class="field"
        .key=${key + (context.suffix ?? '')}
        .schema=${schema}
        .data=${data}
        .context=${{ ...this.context, ...context }}
        .env=${this.env}
      >
      </om2-form-field>
    `;
  }

  render() {
    return html`
      <omni-style>
        <form
          @input="${this._onFormInput}"
          @change="${e => e.stopPropagation()}"
          @submit="${this._submitOrAlert}"
          @om2-add-field-change-listener="${this._addFieldChangeListener}"
        >
          <fieldset>
            ${this._renderUiItems(this.uiSchema, {
              ...this.data,
              ...this.changes,
            })}
          </fieldset>
          <fieldset
            class="is-flex is-justify-content-flex-end mt-5 ${this.hideSubmit
              ? 'is-hidden'
              : ''}"
          >
            <slot name="additional-buttons">
              <button @click=${this._cancel} class="button is-text">
                Cancel
              </button>
            </slot>
            <slot name="submit-button">
              <button
                type="submit"
                class="button is-primary ml-3 ${this.isSubmitting
                  ? 'is-loading'
                  : ''}"
              >
                ${this.submitButtonText ?? 'Save'}
              </button>
            </slot>
          </fieldset>
        </form>
      </omni-style>
    `;
  }
}

Om2Form.register();
